import React, { useState } from 'react'

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Snackbar from '@mui/material/Snackbar';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Skeleton from '@mui/material/Skeleton';
import MuiAlert from '@mui/material/Alert';

import * as Sentry from '@sentry/react'

import rpc, { buildRequest, signoutAndRedirectToIndex } from 'lib/rpc'

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export default function AttachAssignmentForm(props) {

  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  // The Snackbar reports failures too now, so it can't be permanently green.
  const [severity, setSeverity] = useState("success");
  // '' not null: the TextField below is controlled by this value, and a null
  // start makes React treat the input as uncontrolled until the first keystroke.
  const [assignmentId, setAssignmentId] = useState('')

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpen(false);
  };

  const action = (
    <React.Fragment>
      <IconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={handleClose}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </React.Fragment>
  );

  const submitForm = (e) => {
    e.preventDefault()

    rpc.post(
      "",
      buildRequest(
        "admin.assignment.attach",
        {
          lesson_id: props.lessonId,
          assignment_id: assignmentId,
        },
      )
    ).then(response => {
      if (!response.data.result.message) {
        if (response.data.result.id) {
          setMessage(`Assignment [${assignmentId}] attached to the Lesson [${props.lessonId}]`)
          setSeverity('success')
          // The field is controlled, so clearing the state clears the input.
          // There used to be an `e.target.reset()` here as well, which blew up
          // in production: the Attach button also had `onClick={submitForm}`,
          // so on a click `e` was the *click* event and `e.target` the button,
          // which has no `.reset()`. It threw inside this `.then()`, i.e. as an
          // unhandled rejection after the attach had already succeeded.
          setAssignmentId('')
          props.refreshLesson(props.lessonId)
          setOpen(true)
        } else {
          setMessage(`Assignment [${assignmentId}] is already attached to the Lesson [${props.lessonId}]`)
          setSeverity('info')
          setOpen(true)
        }
      } else {
        if (response.data.result.code === 5) {
          signoutAndRedirectToIndex()
        }
        setMessage(response.data.result.message)
        setSeverity('error')
        console.error(response.data.result)
        setOpen(true)
      }
    }).catch(error => {
      // Without this a network failure is another unhandled rejection, and the
      // teacher sees a button that did nothing.
      //
      // But catching it takes the error away from Bugsink: an *un*handled
      // rejection is reported by the SDK's global handler, which is exactly
      // how the crash this PR fixes was found. So report it explicitly —
      // same rule as the backend's `sentry_sdk.capture_exception` in an
      // `except` that turns an error into a response. `beforeSend` still
      // drops RPC_3/RPC_5 as protocol answers.
      Sentry.captureException(error)
      console.error(error)
      setMessage('Не вдалося прикріпити завдання. Спробуйте ще раз.')
      setSeverity('error')
      setOpen(true)
    })
  }

  if (props.lessonId) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <Grid container>
          <Grid item xs={12}>
            <Typography variant="overline" gutterBottom>
              Attach Assignment to the Lesson
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <form onSubmit={submitForm} method="post">
              <Stack direction="row" spacing={2}>
                <TextField
                  type="number"
                  id="assignment-id"
                  label="AssignmentID"
                  variant="standard"
                  value={assignmentId}
                  onChange={(e) => { setAssignmentId(e.target.value) }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  disabled={!assignmentId}
                >
                  Attach
                </Button>
              </Stack>
            </form>
          </Grid>
        </Grid>

        <Snackbar
          open={open}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center"
          }}
          // 2s is a glance at a green tick; it is not enough to read a
          // failure. Success and "already attached" keep the flash.
          autoHideDuration={severity === 'error' ? 6000 : 2000}
          onClose={handleClose}
          action={action}
        >
          <Alert onClose={handleClose} severity={severity} sx={{ width: '100%' }}>
            {message}
          </Alert>
        </Snackbar>
      </Box>
    )
  } else {
    return (
      <Stack spacing={1}>
        {/* For variant="text", adjust the height via font-size */}
        <Skeleton variant="text" sx={{ fontSize: '1.1rem' }} />
        {/* For other variants, adjust the size with `width` and `height` */}
        <Stack direction="row" spacing={2}>
          <Skeleton variant="rectangular" width={200} height={40} />
          <Skeleton variant="rectangular" width={200} height={40} />
        </Stack>
      </Stack>
    )
  }
}
